import { useContext, useState } from "react";
import IKit_Builder_Option from "@models/IKit_Builder_Option";
import { OwAccordion } from "@components/shared/OwAccordion/OwAccordion";
import "./KitBuilderOption.scss";
import OwLabel from "@components/shared/OwLabel/OwLabel";
import IKit_Builder_Option_ItemUI from "@models/IKit_Builder_Option_ItemUI";
import { KitBuilderSubItem } from "../KitBuilderSubItem/KitBuilderSubItem";
import { v4 } from "uuid";
import SupplierContext from "@contexts/SupplierContext";
import HelperService from "@services/HelperService";
import IKit_Builder_Option_Item_SelectionUI from "@models/IKit_Builder_Option_Item_SelectionUI";
import OwButton from "@components/shared/OwButton/OwButton";
import OwSelectOption from "@components/shared/OwSelectOption/OwSelectOption";

type Props = {
    option: IKit_Builder_Option;
    updateFn: (option: IKit_Builder_Option, itemSelections: IKit_Builder_Option_Item_SelectionUI[]) => void;
    showPrice: boolean;
    priceGroupName?: string;
};

type SelectionRow = {
    rowId: string;
    selectedItem: IKit_Builder_Option_ItemUI | null;
    selectedSubItem: IKit_Builder_Option_ItemUI | null;
    qty: number;
};

export const KitBuilderOption = (props: Props) => {
    const supplierModel = useContext(SupplierContext);

    const [selectionRows, setSelectionRows] = useState<SelectionRow[]>([
        {
            rowId: v4(),
            selectedItem: null,
            selectedSubItem: null,
            qty: 1,
        },
    ]);
    const [isOpen, setIsOpen] = useState(false);

    // Calculate total selected across all rows
    const totalSelected = selectionRows.reduce((total, row) => {
        return total + (row.selectedItem ? row.qty : 0);
    }, 0);

    // Get available options for dropdown (filter out selected if allow_repeats is false)
    const getAvailableOptions = (currentRowId: string) => {
        const allOptions = props.option.Kit_Builder_Option_Items || [];

        if (props.option.allow_repeats) {
            return allOptions;
        }

        // Filter out items already selected in other rows
        const selectedItemIds = selectionRows
            .filter((row) => row.rowId !== currentRowId && row.selectedItem)
            .map((row) => row.selectedItem!.id);

        const filteredOptions = allOptions.filter((item) => item.id !== undefined && !selectedItemIds.includes(item.id));

        // Always ensure the currently selected item for this row is included
        const currentRow = selectionRows.find((row) => row.rowId === currentRowId);
        if (currentRow?.selectedItem && !filteredOptions.find((item) => item.id === currentRow.selectedItem!.id)) {
            filteredOptions.push(currentRow.selectedItem);
        }

        return filteredOptions;
    };

    const outOf = props.option.max_allowed ? (
        <div>
            {totalSelected}
            <OwLabel title={"of"} className={"ms-2 me-2"} isLowercase={true} />
            {props.option.max_allowed}
            {props.option.min_required ? <OwLabel title={"required"} className={"mx-2 text-danger"} /> : null}
        </div>
    ) : (
        <div></div>
    );

    const titleElem = (
        <div className={"d-flex align-items-center"}>
            <h6 className={"ms-0 mt-0 mb-0 me-2 text-underline"}>
                <b>{props.option.name}</b>
            </h6>
            {outOf}
        </div>
    );

    // Handle item selection from dropdown
    const handleItemSelect = (rowId: string, selectedItem: IKit_Builder_Option_ItemUI | null) => {
        const updatedRows = selectionRows.map((row) => {
            if (row.rowId === rowId) {
                return {
                    ...row,
                    selectedItem,
                    selectedSubItem: null, // Reset sub-item when main item changes
                    qty: selectedItem ? row.qty : 1,
                };
            }
            return row;
        });

        setSelectionRows(updatedRows);
        updateParent(updatedRows);
    };

    // Handle quantity change for a row
    const handleQuantityChange = (rowId: string, delta: number) => {
        const updatedRows = selectionRows.map((row) => {
            if (row.rowId === rowId) {
                const newQty = Math.max(1, row.qty + delta);

                // Check if total would exceed max_allowed
                const otherRowsTotal = selectionRows.filter((r) => r.rowId !== rowId && r.selectedItem).reduce((sum, r) => sum + r.qty, 0);

                if (props.option.max_allowed > 0 && otherRowsTotal + newQty > props.option.max_allowed) {
                    return row; // Don't update if it would exceed the limit
                }

                return { ...row, qty: newQty };
            }
            return row;
        });

        setSelectionRows(updatedRows);
        updateParent(updatedRows);
    };

    // Add a new selection row
    const addRow = () => {
        setSelectionRows([
            ...selectionRows,
            {
                rowId: v4(),
                selectedItem: null,
                selectedSubItem: null,
                qty: 1,
            },
        ]);
    };

    // Delete a selection row
    const deleteRow = (rowId: string) => {
        const updatedRows = selectionRows.filter((row) => row.rowId !== rowId);
        // Ensure at least one row remains
        if (updatedRows.length === 0) {
            updatedRows.push({
                rowId: v4(),
                selectedItem: null,
                selectedSubItem: null,
                qty: 1,
            });
        }
        setSelectionRows(updatedRows);
        updateParent(updatedRows);
    };

    // Handle sub-item selection
    const handleSubItemSelect = (rowId: string, subItem: IKit_Builder_Option_ItemUI) => {
        const updatedRows = selectionRows.map((row) => {
            if (row.rowId === rowId) {
                return { ...row, selectedSubItem: subItem };
            }
            return row;
        });

        setSelectionRows(updatedRows);
        updateParent(updatedRows);
    };

    // Convert row structure to parent's expected format
    const updateParent = (rows: SelectionRow[]) => {
        const itemSelections: IKit_Builder_Option_Item_SelectionUI[] = [];

        rows.filter((row) => row.selectedItem).forEach((row) => {
            // Create a single entry with the actual quantity
            itemSelections.push({
                id: row.selectedItem!.id,
                uuid: row.rowId,
                item: row.selectedItem!,
                selected: true,
                qty: row.qty, // Pass the actual quantity
                selectedSubItem: row.selectedSubItem,
            });
        });

        props.updateFn(props.option, itemSelections);
    };

    // Check if we can add more rows
    const canAddRow = () => {
        // Don't show add button if only 1 total option is allowed
        if (props.option.max_allowed === 1) {
            return false;
        }

        // Check if there are available options
        const availableOptions = getAvailableOptions("");
        if (availableOptions.length === 0) return false;

        // Check max_allowed limit
        if (props.option.max_allowed > 0 && totalSelected >= props.option.max_allowed) {
            return false;
        }

        return true;
    };

    return (
        <div className={"KitBuilderOption mb-1"}>
            <OwAccordion
                className={"border-0"}
                leftElem={titleElem}
                chevronSide={"RIGHT"}
                fullWidthClick={true}
                openOverride={isOpen}
                titleClick={true}
                fn={() => {
                    setIsOpen(!isOpen);
                }}>
                <div className={"ms-4"}>
                    {selectionRows.map((row) => {
                        const availableOptions = getAvailableOptions(row.rowId);
                        const showSubItems = row.selectedItem && row.selectedItem.Sub_Kit_Builder_Option_Items?.length;

                        return (
                            <div key={row.rowId} className={"mb-3"}>
                                <div className={"d-flex align-items-center gap-2"}>
                                    {/* Option Dropdown */}
                                    <div className={"flex-grow-1"}>
                                        <OwSelectOption
                                            options={availableOptions}
                                            valueProp="id"
                                            titleProp="Catalog_Item.name"
                                            titlePropTwo="Catalog_Item.description"
                                            titleRenderFn={(option) => {
                                                const name = option.Catalog_Item?.name || "";
                                                const desc = option.Catalog_Item?.description || "";
                                                const price =
                                                    props.showPrice &&
                                                    props.priceGroupName &&
                                                    option.Catalog_Item?.pricing?.[props.priceGroupName]
                                                        ? ` - ${HelperService.formatCurrency({
                                                              value: option.Catalog_Item.pricing[props.priceGroupName],
                                                              currency: "USD",
                                                              precision: supplierModel.pricing_precision || 2,
                                                          })}`
                                                        : "";
                                                return `${name} - ${desc}${price}`;
                                            }}
                                            selectedOption={row.selectedItem}
                                            selectedOptionProp="id"
                                            fn={(selectedOption: any) => handleItemSelect(row.rowId, selectedOption)}
                                            name={`kit_option_${props.option.id}_${row.rowId}`}
                                            emitObject={true}
                                            showBlankFirst={true}
                                            autoSelectSingleOption={false}
                                        />
                                    </div>

                                    {/* Quantity Controls */}
                                    {props.option.allow_repeats && row.selectedItem ? (
                                        <div className={"d-flex align-items-center"}>
                                            <OwButton
                                                disableDblClickPrevention={true}
                                                className={"btn-outline-secondary btn-sm"}
                                                fn={() => handleQuantityChange(row.rowId, -1)}
                                                disabled={row.qty <= 1}>
                                                -
                                            </OwButton>
                                            <span className={"mx-2 fw-bold"}>{row.qty}</span>
                                            <OwButton
                                                disableDblClickPrevention={true}
                                                className={"btn-outline-secondary btn-sm"}
                                                fn={() => handleQuantityChange(row.rowId, 1)}
                                                disabled={
                                                    props.option.max_allowed > 0 &&
                                                    totalSelected - row.qty + row.qty + 1 > props.option.max_allowed
                                                }>
                                                +
                                            </OwButton>
                                        </div>
                                    ) : null}

                                    {/* Delete Button */}
                                    {selectionRows.length > 1 || (selectionRows.length === 1 && !props.option.min_required) ? (
                                        <OwButton className={"btn-outline-danger btn-sm"} fn={() => deleteRow(row.rowId)}>
                                            <i className="fa fa-trash" />
                                        </OwButton>
                                    ) : null}
                                </div>

                                {/* Sub-items */}
                                {showSubItems && row.selectedItem ? (
                                    <div className={"ms-5 mt-2"}>
                                        <KitBuilderSubItem
                                            item={row.selectedItem}
                                            subSelectFn={(_, subItem) => handleSubItemSelect(row.rowId, subItem)}
                                            showPrice={props.showPrice}
                                            priceGroupName={props.priceGroupName}
                                        />
                                    </div>
                                ) : null}
                            </div>
                        );
                    })}

                    {/* Add Row Button */}
                    {canAddRow() ? (
                        <div className={"mt-3"}>
                            <OwButton className={"btn-outline-primary btn-sm"} fn={addRow}>
                                <i className="fa fa-plus me-2" />
                                Add Another Option
                            </OwButton>
                        </div>
                    ) : null}
                </div>
            </OwAccordion>
        </div>
    );
};
